/**
 * V2 On the way — /route (2026-08-03), route × sets.
 *
 * "I'm driving I-40 — which stops complete my sets?" Start and End
 * resolve through the usual place autocomplete (cities welcome — the
 * area rejection is a logging rule, not a routing one), the proxy's
 * directions action returns the polyline, and everything after that is
 * client-side corridor math (route-math.js) over two candidate pools:
 * the Want to go list, and un-visited places from FOLLOWED sets only —
 * personal by design, and it keeps the query volume sane.
 *
 * The URL carries from/to/km, so a route link texted to a spouse
 * renders the same drive; the saved-places group is simply theirs (or
 * absent, signed out). One directions call per route — chip changes
 * re-filter the kept candidates without touching the network.
 */
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import * as gateway from '../../core/gateway.js';
import { searchGooglePlaces, placeDetails, fetchRoute } from '../log/places.js';
import { decodePolyline, inCorridor } from './route-math.js';
import { fetchSavedPlaces } from '../lists/lists.js';
import { fetchSubtreeYoinks } from '../category/category-data.js';
import { mountMap, mapsAvailable, getUserLocation } from '../../ui/map-view.js';
import { getUrlParam, setUrlParam } from '../../ui/share.js';
import { loadRoutes, rememberRoute, forgetRoute } from './saved-routes.js';

// 10 km is what "on the way" honestly means — roughly a ten-minute
// detour. 50 km was a 40-minute round trip dressed up as a stop, and it
// padded results with places nobody would actually pull off for, so it's
// gone (founder call 2026-08-03). 25 stays as the one widen-if-empty
// step, which sparse sets (parks are far apart) genuinely need.
const WIDTHS_KM = [10, 25];
const DEFAULT_WIDTH_KM = 10;
const MAX_WIDTH_KM = WIDTHS_KM[WIDTHS_KM.length - 1];

/** "12345s" → "3 h 26 min" (Google's Routes duration format). */
export function formatDuration(duration) {
  const seconds = parseInt(duration, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
}

function parseEndpoint(coords, name) {
  const m = (coords || '').match(/^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  return { name: name || 'Pinned spot', lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  const endpoints = {
    from: parseEndpoint(getUrlParam('from'), getUrlParam('fromname')),
    to: parseEndpoint(getUrlParam('to'), getUrlParam('toname')),
  };
  let widthKm = WIDTHS_KM.includes(Number(getUrlParam('km'))) ? Number(getUrlParam('km')) : DEFAULT_WIDTH_KM;
  let busy = false;
  let error = '';
  let errorDetail = ''; // the server's own words — the iPad has no console
  let raw = null; // { path, route, saved: [cand], sets: [{id,name,total,mine,remaining:[cand]}] }
  let mapHandle = null;
  let userId = store.get().user?.id || null;
  let savedRoutes = loadRoutes(userId);

  function endpointField(key, label) {
    const value = endpoints[key];
    return `
      <div class="route-field">
        <label class="v2-field">${label}
          <input type="search" data-route-input="${key}" autocomplete="off" enterkeyhint="search"
            placeholder="City, park, address…" value="${escapeHtml(value?.name || '')}">
        </label>
        ${key === 'from' ? `<button type="button" class="list-link-alt-toggle" data-route-locate>
          Use my location</button>` : ''}
        <div class="log-place-results" data-route-results="${key}" aria-live="polite"></div>
      </div>`;
  }

  function hitRow(hit) {
    return `
      <button type="button" class="route-hit" data-route-loc="${escapeHtml(hit.id)}">
        <span class="route-hit-name">${escapeHtml(hit.name || 'A place')}${hit.closedTemporarily
          ? '<span class="list-entry-closed list-entry-closed-temp">Temporarily closed</span>' : ''}</span>
        <span class="route-hit-detour">${hit.detourKm < 1 ? 'right on the road' :
          `${Math.round(hit.detourKm)} km off the road`}</span>
      </button>`;
  }

  function resultsHtml() {
    if (busy) return '<div class="v2-loading">Drawing the route…</div>';
    if (error) {
      return `<div class="v2-empty">${escapeHtml(error)}</div>
        ${errorDetail ? `<p class="log-hint route-error-detail">The server said: “${escapeHtml(errorDetail)}”</p>` : ''}`;
    }
    if (!raw) return '';
    const savedHits = inCorridor(raw.saved, raw.path, widthKm);
    const sets = raw.sets
      .map((s) => ({ ...s, hits: inCorridor(s.remaining, raw.path, widthKm) }))
      .filter((s) => s.hits.length);
    const km = raw.route.distanceMeters ? `${Math.round(raw.route.distanceMeters / 1000)} km` : '';
    const time = formatDuration(raw.route.duration);
    const signedIn = !!store.get().user;
    const follows = (store.get().followedCategoryIds || []).length;
    return `
      <div class="route-results">
        <p class="route-summary">${escapeHtml([km, time && `about ${time}`].filter(Boolean).join(' · '))}</p>
        ${mapsAvailable() ? '<div class="v2-map" data-route-map></div>' : ''}
        ${savedHits.length ? `
        <section class="route-group" aria-label="Saved places on the way">
          <h3 class="friends-title">On your Want to go list (${savedHits.length})</h3>
          ${savedHits.map(hitRow).join('')}
        </section>` : ''}
        ${sets.map((s) => `
        <section class="route-group" aria-label="${escapeHtml(s.name)} on the way">
          <h3 class="friends-title">${escapeHtml(s.name)} — you’re at ${s.mine} of ${s.total}; ${s.hits.length} more on this drive</h3>
          ${s.hits.map(hitRow).join('')}
        </section>`).join('')}
        ${!savedHits.length && !sets.length ? `
        <p class="v2-empty">Nothing within ${widthKm} km of this drive${signedIn ? '' :
          ' — sign in (top right) to check your own saved places and sets'}.
          ${widthKm !== MAX_WIDTH_KM ? 'Try a wider detour above.' : ''}</p>` : ''}
        ${signedIn && !follows ? `
        <p class="log-hint">Following a collection makes this page sing — every un-visited place
          along your drive shows up here. <a href="${basePath}/categories" data-route-browse>Browse collections</a></p>` : ''}
      </div>`;
  }

  /**
   * The drives this device remembers. Sits after the results so a fresh
   * arrival (no results yet) finds it right under the form — the moment
   * it's actually wanted — without pushing results down later.
   */
  function savedRoutesHtml() {
    if (!savedRoutes.length) return '';
    return `
      <section class="route-saved" aria-label="Saved routes">
        <h3 class="friends-title">Saved routes</h3>
        <p class="log-hint">Tap one to run it again. Kept on this device.</p>
        ${savedRoutes.map((r) => `
        <div class="route-saved-row">
          <button type="button" class="route-saved-open" data-route-open="${escapeHtml(r.id)}">
            <span class="route-saved-name">${escapeHtml(r.fromName)} → ${escapeHtml(r.toName)}</span>
            <span class="route-saved-km">${r.km} km detour</span>
          </button>
          <button type="button" class="route-saved-forget" data-route-forget="${escapeHtml(r.id)}"
            aria-label="Forget ${escapeHtml(r.fromName)} to ${escapeHtml(r.toName)}">×</button>
        </div>`).join('')}
      </section>`;
  }

  function render() {
    el.innerHTML = `
      <section class="v2-route" aria-label="On the way">
        <h2 class="v2-page-title">On the way</h2>
        <p>Where are you driving? Yoink checks the road for places you’ve saved
          and stops that complete your collections.</p>
        ${endpointField('from', 'Start')}
        ${endpointField('to', 'End')}
        <div class="v2-toggles route-widths">
          <span class="route-widths-label">Worth a detour:</span>
          <div class="v2-scope" role="group" aria-label="Detour width">
            ${WIDTHS_KM.map((w) => `
            <button type="button" class="v2-scope-btn ${w === widthKm ? 'scope-active' : ''}"
              data-route-width="${w}" aria-pressed="${w === widthKm}">${w} km</button>`).join('')}
          </div>
        </div>
        <button type="button" class="v2-btn-leather" data-route-go
          ${endpoints.from && endpoints.to && !busy ? '' : 'disabled'}>Find places on the way</button>
        ${resultsHtml()}
        ${savedRoutesHtml()}
      </section>`;
    mapHandle?.cleanup();
    mapHandle = null;
    const mapHost = el.querySelector('[data-route-map]');
    if (mapHost && raw) {
      // Corridor pins are saved places / uncollected set members, not
      // logs — the "N yoinks" count line would lie here (2026-08-04)
      mapHandle = mountMap(mapHost, { store, router, countNoun: null });
      if (mapsAvailable()) {
        const savedHits = inCorridor(raw.saved, raw.path, widthKm);
        const setHits = raw.sets.flatMap((s) => inCorridor(s.remaining, raw.path, widthKm));
        const seen = new Set();
        const pins = [...savedHits, ...setHits].filter((h) => {
          if (seen.has(h.id)) return false;
          seen.add(h.id);
          return true;
        });
        mapHandle.setYoinks(pins.map((h) => ({
          locations: { id: h.id, display_name: h.name, lat: h.lat, lng: h.lng },
        })));
        mapHandle.setPath(raw.path);
      }
    }
  }

  async function runSearch(key, term) {
    const resultsEl = el.querySelector(`[data-route-results="${key}"]`);
    if (!resultsEl) return;
    if (term.trim().length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    const suggestions = await searchGooglePlaces(term.trim()).catch(() => []);
    if (destroyed) return;
    if (el.querySelector(`[data-route-input="${key}"]`)?.value.trim() !== term.trim()) return;
    resultsEl.innerHTML = suggestions.slice(0, 5).map((s) => `
      <button type="button" class="log-place-option" data-route-pick="${key}"
        data-pick-id="${escapeHtml(s.placeId)}" data-pick-name="${escapeHtml(s.mainText)}">
        <strong>${escapeHtml(s.mainText)}</strong>
        ${s.secondaryText ? `<span>${escapeHtml(s.secondaryText)}</span>` : ''}
      </button>`).join('') ||
      '<div class="log-hint">Nothing by that name — try another spelling.</div>';
  }

  async function pickEndpoint(key, placeId, name) {
    try {
      const details = await placeDetails(placeId, 'displayName,location');
      const lat = details.location?.latitude;
      const lng = details.location?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') throw new Error();
      endpoints[key] = { name: details.displayName?.text || name, lat, lng };
      raw = null;
      error = '';
      errorDetail = '';
      render();
    } catch {
      showToast('Couldn’t pin that spot — try another.');
    }
  }

  /**
   * status for the given location ids, chunked so a long in.() list can't
   * blow the URL length. Missing rows just come back absent — a status we
   * couldn't read is treated as open rather than hiding a real place.
   */
  async function fetchStatuses(ids) {
    const map = new Map();
    for (let i = 0; i < ids.length; i += 150) {
      const chunk = ids.slice(i, i + 150);
      const { data } = await gateway.rawQuery(gateway.TABLES.LOCATIONS, {
        select: 'id,status',
        filters: { id: `in.(${chunk.join(',')})` },
        limit: chunk.length,
      }).catch(() => ({ data: [] }));
      (data || []).forEach((row) => map.set(row.id, row.status));
    }
    return map;
  }

  async function runRoute() {
    if (!endpoints.from || !endpoints.to || busy) return;
    busy = true;
    error = '';
    errorDetail = '';
    render();
    try {
      const route = await fetchRoute(
        { lat: endpoints.from.lat, lng: endpoints.from.lng },
        { lat: endpoints.to.lat, lng: endpoints.to.lng }
      );
      const path = decodePolyline(route.encoded);
      if (!path.length) throw new Error('No drivable route found.');

      const savedEntries = await fetchSavedPlaces(store).catch(() => []);
      const saved = savedEntries
        .map((e) => e.locations)
        .filter((l) => l?.id)
        .map((l) => ({ id: l.id, name: l.display_name, lat: l.lat, lng: l.lng, status: l.status }));

      const me = store.get().user;
      const catIds = store.get().followedCategoryIds || [];
      let cats = [];
      if (catIds.length) {
        const { data } = await gateway.rawQuery(gateway.TABLES.CATEGORIES, {
          select: 'id,name',
          filters: { id: `in.(${catIds.join(',')})` },
          limit: 100,
        });
        cats = data || [];
      }
      const sets = [];
      for (const cat of cats) {
        const rows = await fetchSubtreeYoinks(cat.id).catch(() => []);
        const byLoc = new Map();
        const mine = new Set();
        for (const r of rows) {
          if (!r.location_id || !r.locations) continue;
          byLoc.set(r.location_id, r.locations);
          if (me && r.user_id === me.id) mine.add(r.location_id);
        }
        const remaining = [...byLoc.entries()]
          .filter(([id]) => !mine.has(id))
          .map(([id, l]) => ({ id, name: l.display_name, lat: l.lat, lng: l.lng }));
        if (byLoc.size) {
          sets.push({ id: cat.id, name: cat.name, total: byLoc.size, mine: mine.size, remaining });
        }
      }

      // Closed places must not be offered as stops. The subtree RPC's
      // jsonb carries no status, so ask for it — but only for the places
      // that could actually surface (the corridor at its widest), which
      // keeps this to one small query instead of thousands of ids.
      const reachable = [
        ...inCorridor(saved, path, MAX_WIDTH_KM),
        ...sets.flatMap((s) => inCorridor(s.remaining, path, MAX_WIDTH_KM)),
      ];
      const statusById = await fetchStatuses(
        [...new Set(reachable.filter((c) => c.status === undefined).map((c) => c.id))]
      );
      const statusOf = (c) => c.status ?? statusById.get(c.id);
      const openOnly = (list) => list
        .filter((c) => statusOf(c) !== 'closed_permanently')
        .map((c) => ({ ...c, closedTemporarily: statusOf(c) === 'closed_temporarily' }));

      raw = {
        path,
        route,
        saved: openOnly(saved),
        sets: sets.map((s) => ({ ...s, remaining: openOnly(s.remaining) })),
      };
      // A drive that plotted is a drive worth coming back to
      savedRoutes = rememberRoute(userId, {
        fromName: endpoints.from.name,
        fromLat: endpoints.from.lat,
        fromLng: endpoints.from.lng,
        toName: endpoints.to.name,
        toLat: endpoints.to.lat,
        toLng: endpoints.to.lng,
        km: widthKm,
      });
      setUrlParam('from', `${endpoints.from.lat},${endpoints.from.lng}`);
      setUrlParam('fromname', endpoints.from.name);
      setUrlParam('to', `${endpoints.to.lat},${endpoints.to.lng}`);
      setUrlParam('toname', endpoints.to.name);
      setUrlParam('km', String(widthKm));
    } catch (err) {
      raw = null;
      if (err?.message === 'No drivable route found.') {
        error = 'No drivable route between those two — try different endpoints.';
        errorDetail = '';
      } else {
        error = 'Couldn’t plot that route just now — try again.';
        // Google's/the proxy's own message, on-page: "Routes API has not
        // been used in project…" is a Cloud Console fix, not a code bug,
        // and the iPad can't open a console to find that out.
        errorDetail = err?.message || '';
      }
    }
    busy = false;
    if (!destroyed) render();
  }

  async function locate(button) {
    button.disabled = true;
    const here = await getUserLocation();
    if (destroyed) return;
    if (!here) {
      if (button.isConnected) button.disabled = false;
      showToast('Couldn’t get a fix — type a starting point instead.');
      return;
    }
    endpoints.from = { name: 'My location', lat: here.lat, lng: here.lng };
    raw = null;
    render();
  }

  function onClick(event) {
    const pick = event.target.closest('[data-route-pick]');
    if (pick) {
      pickEndpoint(pick.dataset.routePick, pick.dataset.pickId, pick.dataset.pickName);
      return;
    }
    if (event.target.closest('[data-route-locate]')) {
      locate(event.target.closest('[data-route-locate]'));
      return;
    }
    const width = event.target.closest('[data-route-width]');
    if (width) {
      widthKm = Number(width.dataset.routeWidth);
      setUrlParam('km', String(widthKm));
      render(); // corridor re-filters from kept candidates — no network
      return;
    }
    if (event.target.closest('[data-route-go]')) {
      runRoute();
      return;
    }
    const forget = event.target.closest('[data-route-forget]');
    if (forget) {
      savedRoutes = forgetRoute(userId, forget.dataset.routeForget);
      render();
      return;
    }
    const open = event.target.closest('[data-route-open]');
    if (open) {
      const saved = savedRoutes.find((r) => r.id === open.dataset.routeOpen);
      if (saved) {
        endpoints.from = { name: saved.fromName, lat: saved.fromLat, lng: saved.fromLng };
        endpoints.to = { name: saved.toName, lat: saved.toLat, lng: saved.toLng };
        if (WIDTHS_KM.includes(saved.km)) widthKm = saved.km;
        raw = null;
        error = '';
        errorDetail = '';
        runRoute();
      }
      return;
    }
    const hit = event.target.closest('[data-route-loc]');
    if (hit) {
      router.navigate('location', { id: hit.dataset.routeLoc });
      return;
    }
    const browse = event.target.closest('[data-route-browse]');
    if (browse) {
      event.preventDefault();
      router.navigate('categories');
    }
  }

  const onInput = debounce((event) => {
    const input = event.target.closest('[data-route-input]');
    if (!input) return;
    endpoints[input.dataset.routeInput] = null; // typing invalidates the pin
    runSearch(input.dataset.routeInput, input.value);
  }, 300);

  // Auth settles after first paint: until it does, this page is reading
  // the signed-out bucket, so a returning explorer would see an empty
  // list and save their next drive into the wrong one.
  const unsubscribe = store.subscribe((s) => {
    if (destroyed) return;
    const uid = s.user?.id || null;
    if (uid === userId) return;
    userId = uid;
    savedRoutes = loadRoutes(userId);
    render();
  });

  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
  render();
  // A shared link arrives with both pins — run it without another tap.
  if (endpoints.from && endpoints.to) runRoute();

  return () => {
    destroyed = true;
    unsubscribe();
    mapHandle?.cleanup();
    el.removeEventListener('click', onClick);
    el.removeEventListener('input', onInput);
  };
}
